import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  DwConfigService,
  DwConfigServiceToken,
  DwFieldAggregate,
  DwFieldType,
  DwOrmDataService,
  DwOrmDataServiceToken
} from '@devwareapps/devware-cap';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import {
  AIProcessingReferenceEntity,
  AIProcessingReferenceStatusAllItems,
  AppMetaData,
  AppMetaDataLookups,
  CourseDocumentItemEntity,
  CourseEntity,
  CourseLessonEntity,
  CourseLessonDocumentLinkEntity,
  CourseModuleEntity,
  DocumentItemEntity,
  DocumentLinkProcessingRunEntity,
  DocumentLinkProcessingRunItemEntity,
  DocumentLinkProcessingRunItemStatusAllItems,
  DocumentLinkProcessingRunStatusAllItems,
  DocumentPublicationEntity,
  DocumentSectionEntity,
  DocumentSectionQueryBuilder,
  ReferenceStatusAllItems
} from '../../../meta-data/app-meta-data.service';
import {
  FarAimProcessingCourse,
  FarAimLinkReprocessRequest,
  FarAimLinkReprocessResult,
  FarAimProcessingLesson,
  FarAimProcessingModule,
  FarAimProcessingIssue,
  FarAimProcessingStage,
  FarAimProcessingSummary
} from '../models/far-aim-processing.model';

interface FarAimVectorSummary {
  ActiveSectionCount: number;
  VectorEligibleSectionCount: number;
  IndexedSectionCount: number;
  NonVectorSectionCount: number;
}

interface FarAimLegacySectionAggregate extends DocumentSectionEntity {
  SectionCount: number;
  IndexedSectionCount: number;
}

@Injectable({ providedIn: 'root' })
export class FarAimProcessingRepositoryService {
  private readonly documentsApi: string;
  private readonly useStructuredVectorSummaryApi = true;

  constructor(
    @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
    @Inject(DwConfigServiceToken) configService: DwConfigService,
    private http: HttpClient
  ) {
    this.documentsApi = `${configService.coreConfig.apiRoot}/aviator-online/documents`;
  }

  queueLinkReprocess(documentItemId: number, request: FarAimLinkReprocessRequest): Observable<FarAimLinkReprocessResult> {
    return this.http.post<FarAimLinkReprocessResult>(
      `${this.documentsApi}/${documentItemId}/structured-linking/reprocess`,
      request
    );
  }

  getSummary(documentItemId: number): Observable<FarAimProcessingSummary> {
    const documentQuery = AppMetaData.DocumentItem.CreateQueryBuilder();
    documentQuery.addFilterAnd(f => f.Equal(a => a.DocumentItemId, documentItemId));

    const publicationQuery = AppMetaData.DocumentPublication.CreateQueryBuilder();
    publicationQuery.addFilterAnd(f => f.Equal(a => a.DocumentItemId, documentItemId));
    publicationQuery.addFilterAnd(f => f.Equal(a => a.ReferenceStatusId, ReferenceStatusAllItems.Active));
    publicationQuery.addPrefetch(r => r.DocumentPublicationType);
    publicationQuery.addPrefetch(r => r.DocumentVersionByCurrentDocumentVersionId);
    publicationQuery.query.FieldSettings.LoadAllLookupDisplayFields = true;

    const courseQuery = AppMetaData.CourseDocumentItem.CreateQueryBuilder();
    courseQuery.addFilterAnd(f => f.Equal(a => a.DocumentItemId, documentItemId));
    courseQuery.addFilterAnd(f => f.Equal(a => a.CourseDocumentItemStatusId, ReferenceStatusAllItems.Active));
    courseQuery.addFields(f => [f.CourseDocumentItemId, f.CourseId]);

    const linkQuery = AppMetaData.CourseLessonDocumentLink.CreateQueryBuilder();
    linkQuery.addFilterAnd(f => f.Equal(a => a.DocumentItemId, documentItemId));
    linkQuery.addFilterAnd(f => f.Equal(a => a.CourseLessonDocumentLinkStatusId, ReferenceStatusAllItems.Active));
    linkQuery.addFields(f => [f.CourseLessonDocumentLinkId, f.CourseLessonId, f.CourseLessonDocumentLinkStatusId,
      f.AIConfidence, f.AIMatchReason, f.ReferenceSummaryText]);
    const referenceQuery = AppMetaData.CourseLessonDocumentLinkReference.CreateQueryBuilder();
    referenceQuery.addFilterAnd(f => f.Equal(a => a.ReferenceStatusId, ReferenceStatusAllItems.Active));
    referenceQuery.addFields(f => [f.CourseLessonDocumentLinkReferenceId]);
    linkQuery.addPrefetch(r => r.CourseLessonDocumentLinkReference, referenceQuery.query);

    const runQuery = AppMetaData.DocumentLinkProcessingRun.CreateQueryBuilder();
    runQuery.addFilterAnd(f => f.Equal(a => a.DocumentItemId, documentItemId));
    runQuery.addPrefetch(r => r.DocumentLinkProcessingRunStatus);
    runQuery.addPrefetch(r => r.DocumentLinkProcessingTriggerType);
    const runItemQuery = AppMetaData.DocumentLinkProcessingRunItem.CreateQueryBuilder();
    runItemQuery.addFields(f => [f.DocumentLinkProcessingRunItemId, f.DocumentLinkProcessingRunId,
      f.CourseId, f.CourseModuleId, f.CourseLessonId,
      f.CourseLessonDocumentLinkId, f.AIProcessingReferenceId, f.DocumentLinkProcessingRunItemStatusId,
      f.DocumentLinkSourceTypeId, f.ProgressText, f.ErrorText]);
    const runLinkQuery = AppMetaData.CourseLessonDocumentLink.CreateQueryBuilder();
    runLinkQuery.addFields(f => [f.CourseLessonDocumentLinkId, f.AIConfidence, f.AIMatchReason]);
    const runLinkReferenceQuery = AppMetaData.CourseLessonDocumentLinkReference.CreateQueryBuilder();
    runLinkReferenceQuery.addFilterAnd(f => f.Equal(a => a.ReferenceStatusId, ReferenceStatusAllItems.Active));
    runLinkReferenceQuery.addFields(f => [f.CourseLessonDocumentLinkReferenceId]);
    runLinkQuery.addPrefetch(r => r.CourseLessonDocumentLinkReference, runLinkReferenceQuery.query);
    const runReferenceQuery = AppMetaData.AIProcessingReference.CreateQueryBuilder();
    runReferenceQuery.addFields(f => [f.AIProcessingReferenceId, f.AIProcessingStatusId]);
    runReferenceQuery.query.FieldSettings.LoadAllLookupDisplayFields = true;
    runItemQuery.addPrefetch(r => r.CourseLessonDocumentLink, runLinkQuery.query);
    runItemQuery.addPrefetch(r => r.AIProcessingReference, runReferenceQuery.query);
    runItemQuery.addPrefetch(r => r.DocumentLinkProcessingRunItemStatus);
    runItemQuery.addPrefetch(r => r.DocumentLinkSourceType);
    runQuery.addPrefetch(r => r.DocumentLinkProcessingRunItem, runItemQuery.query);
    runQuery.query.FieldSettings.LoadAllLookupDisplayFields = true;
    runQuery.addOrderByDesc(a => a.DocumentLinkProcessingRunId);
    runQuery.query.PagingInfo = { Top: 1, PageNo: 1, PageSize: 1 };

    return forkJoin([
      this.dwOrmDataService.executeQuerySingle<DocumentItemEntity>(documentQuery),
      this.dwOrmDataService.executeQuery<DocumentPublicationEntity>(publicationQuery),
      this.dwOrmDataService.executeQuery<CourseDocumentItemEntity>(courseQuery),
      this.dwOrmDataService.executeQuery<CourseLessonDocumentLinkEntity>(linkQuery),
      this.dwOrmDataService.executeQuery<DocumentLinkProcessingRunEntity>(runQuery)
    ]).pipe(mergeMap(([documentItem, publications, courseItems, links, runs]) => {
      const publication = (publications || [])[0];
      const versionId = publication?.CurrentDocumentVersionId;
      const sectionQuery = AppMetaData.DocumentSection.CreateQueryBuilder();

      if (versionId && !this.useStructuredVectorSummaryApi) {
        sectionQuery.addFilterAnd(f => f.Equal(a => a.DocumentVersionId, versionId));
        sectionQuery.addFilterAnd(f => f.Equal(a => a.ReferenceStatusId, ReferenceStatusAllItems.Active));
        sectionQuery.query.FieldSettings.AggregateQuery = true;
        sectionQuery.query.AddField({
          FieldType: DwFieldType.Aggregate,
          AggregateFunction: DwFieldAggregate.Count,
          FieldAlias: 'SectionCount',
          FullFieldName: AppMetaData.DocumentSection.Attributes.DocumentSectionId.fieldNameWithItemPrefix
        });
        sectionQuery.query.AddField({
          FieldType: DwFieldType.Aggregate,
          AggregateFunction: DwFieldAggregate.Count,
          FieldAlias: 'IndexedSectionCount',
          FullFieldName: AppMetaData.DocumentSection.Attributes.VectorReferenceId.fieldNameWithItemPrefix
        });
      }
      const courseIds = (courseItems || []).map(item => item.CourseId).filter((id): id is number => !!id);
      const hierarchyQuery = AppMetaData.Course.CreateQueryBuilder();
      hierarchyQuery.addFilterAnd(f => f.InList(a => a.CourseId, courseIds));
      hierarchyQuery.addFields(f => [f.CourseId, f.CourseName]);
      const hierarchyModuleQuery = AppMetaData.CourseModule.CreateQueryBuilder();
      hierarchyModuleQuery.addFilterAnd(f => f.Equal(a => a.ModuleStatusId, ReferenceStatusAllItems.Active));
      hierarchyModuleQuery.addFields(f => [f.CourseModuleId, f.CourseId, f.ModuleName, f.ModuleOrder]);
      const hierarchyLessonQuery = AppMetaData.CourseLesson.CreateQueryBuilder();
      hierarchyLessonQuery.addFilterAnd(f => f.Equal(a => a.LessonStatusId, ReferenceStatusAllItems.Active));
      hierarchyLessonQuery.addFields(f => [f.CourseLessonId, f.CourseModuleId, f.LessonName, f.LessonOrder]);
      hierarchyModuleQuery.addPrefetch(r => r.CourseLesson, hierarchyLessonQuery.query);
      hierarchyQuery.addPrefetch(r => r.CourseModule, hierarchyModuleQuery.query);

      const linkIds = (links || [])
        .map(link => link.CourseLessonDocumentLinkId)
        .filter((id): id is number => !!id);

      const aiReferenceQuery = AppMetaData.AIProcessingReference.CreateQueryBuilder();
      aiReferenceQuery.addFilterAnd(f => f.Equal(a => a.MetaDataItemId, AppMetaDataLookups.CourseLessonDocumentLinkAllItems));

      if (linkIds.length) {
        aiReferenceQuery.addFilterAnd(f => f.InList(a => a.KeyValue, linkIds));
      }

      aiReferenceQuery.query.FieldSettings.LoadAllLookupDisplayFields = true;
      aiReferenceQuery.addFields(f => [f.AIProcessingReferenceId, f.KeyValue, f.AIProcessingStatusId, f.ModifiedDateTime]);

      return forkJoin([
        versionId
          ? this.getVectorSummary(documentItemId, versionId, sectionQuery)
          : of({ ActiveSectionCount: 0, VectorEligibleSectionCount: 0, IndexedSectionCount: 0,
            NonVectorSectionCount: 0 } as FarAimVectorSummary),
        linkIds.length ? this.dwOrmDataService.executeQuery<AIProcessingReferenceEntity>(aiReferenceQuery) : of([] as AIProcessingReferenceEntity[]),
        courseIds.length ? this.dwOrmDataService.executeQuery<CourseEntity>(hierarchyQuery) : of([] as CourseEntity[])
      ]).pipe(map(([sectionCounts, aiReferences, courseHierarchy]) => this.mapSummary(
        documentItem,
        publication,
        courseItems || [],
        courseHierarchy || [],
        links || [],
        sectionCounts,
        aiReferences || [],
        (runs || []).slice().sort((a, b) => (b.DocumentLinkProcessingRunId || 0) - (a.DocumentLinkProcessingRunId || 0))[0]
      )));
    }));
  }

  private getVectorSummary(
    documentItemId: number,
    documentVersionId: number,
    sectionQuery: DocumentSectionQueryBuilder
  ): Observable<FarAimVectorSummary> {
    if (this.useStructuredVectorSummaryApi) {
      return this.http.get<FarAimVectorSummary>(
        `${this.documentsApi}/${documentItemId}/structured-vector-summary?documentVersionId=${documentVersionId}`
      );
    }

    return this.dwOrmDataService.executeQuery<FarAimLegacySectionAggregate>(sectionQuery).pipe(map(rows => {
      const counts = rows?.[0] || { SectionCount: 0, IndexedSectionCount: 0 } as FarAimLegacySectionAggregate;
      const activeSectionCount = counts.SectionCount || 0;
      const indexedSectionCount = counts.IndexedSectionCount || 0;
      return {
        ActiveSectionCount: activeSectionCount,
        VectorEligibleSectionCount: indexedSectionCount,
        IndexedSectionCount: indexedSectionCount,
        NonVectorSectionCount: Math.max(0, activeSectionCount - indexedSectionCount)
      };
    }));
  }

  private mapSummary(
    documentItem: DocumentItemEntity,
    publication: DocumentPublicationEntity | undefined,
    courseItems: CourseDocumentItemEntity[],
    courseHierarchy: CourseEntity[],
    links: CourseLessonDocumentLinkEntity[],
    sectionCounts: FarAimVectorSummary,
    aiReferences: AIProcessingReferenceEntity[],
    latestRun?: DocumentLinkProcessingRunEntity
  ): FarAimProcessingSummary {
    const aiReferenceByLinkId = new Map<number, AIProcessingReferenceEntity>();
    aiReferences.forEach(reference => {
      if (reference.KeyValue) {
        aiReferenceByLinkId.set(reference.KeyValue, reference);
      }
    });

    const coursesById = new Map<number, FarAimProcessingCourse>();
    const modulesById = new Map<number, CourseModuleEntity>();
    const lessonsById = new Map<number, CourseLessonEntity>();
    courseHierarchy.forEach(course => (course.CourseModule || []).forEach(module => {
      if (module.CourseModuleId) { modulesById.set(module.CourseModuleId, module); }
      (module.CourseLesson || []).forEach(lesson => {
        if (lesson.CourseLessonId) { lessonsById.set(lesson.CourseLessonId, lesson); }
      });
    }));
    courseItems.forEach(item => {
      if (!item.CourseId) {
        return;
      }
      const course = courseHierarchy.find(value => value.CourseId === item.CourseId);
      coursesById.set(item.CourseId, this.createCourse(item.CourseId, course?.CourseName || item.CourseIdDisplay));
    });

    links.forEach(link => {
      const lessonEntity = link.CourseLessonId ? lessonsById.get(link.CourseLessonId) : undefined;
      const moduleEntity = lessonEntity?.CourseModuleId ? modulesById.get(lessonEntity.CourseModuleId) : undefined;
      const courseId = moduleEntity?.CourseId;
      const moduleId = moduleEntity?.CourseModuleId;
      if (!courseId || !moduleId || !coursesById.has(courseId)) {
        return;
      }

      const courseSummary = coursesById.get(courseId)!;

      let moduleSummary = courseSummary.Modules.find(module => module.CourseModuleId === moduleId);
      if (!moduleSummary) {
        moduleSummary = this.createModule(
          moduleId,
          moduleEntity?.ModuleName,
          moduleEntity?.ModuleOrder
        );
        courseSummary.Modules.push(moduleSummary);
      }

      const aiReference = link.CourseLessonDocumentLinkId
        ? aiReferenceByLinkId.get(link.CourseLessonDocumentLinkId)
        : undefined;
      const lesson = this.mapLesson(link, lessonEntity, aiReference);
      moduleSummary.Lessons.push(lesson);
      this.incrementCounts(moduleSummary, lesson);
      this.incrementCounts(courseSummary, lesson);
    });

    if (latestRun) {
      this.applyAuthoritativeRunItems(coursesById, modulesById, lessonsById, latestRun.DocumentLinkProcessingRunItem || []);
      coursesById.forEach(course => {
        course.QueuedCount = 0; course.ProcessingCount = 0; course.FailedCount = 0;
        course.Modules.forEach(module => {
          module.QueuedCount = 0; module.ProcessingCount = 0; module.FailedCount = 0;
          module.Lessons.forEach(lesson => {
            this.incrementWorkflowCounts(module, lesson);
            this.incrementWorkflowCounts(course, lesson);
          });
        });
      });
    }

    const courses = Array.from(coursesById.values())
      .sort((a, b) => (a.CourseName || '').localeCompare(b.CourseName || ''));
    courses.forEach(course => {
      course.Modules.sort((a, b) => (a.ModuleOrder || 0) - (b.ModuleOrder || 0));
      course.Modules.forEach(module => module.Lessons.sort((a, b) => (a.LessonOrder || 0) - (b.LessonOrder || 0)));
    });

    const activeLinks = links.filter(link => link.CourseLessonDocumentLinkStatusId === ReferenceStatusAllItems.Active);
    const directLinkCount = activeLinks.filter(link => !link.AIConfidence).length;
    const aiLinkCount = activeLinks.filter(link => !!link.AIConfidence).length;
    const sectionCount = sectionCounts.ActiveSectionCount || 0;
    const vectorEligibleSectionCount = sectionCounts.VectorEligibleSectionCount || 0;
    const nonVectorSectionCount = sectionCounts.NonVectorSectionCount || 0;
    const indexedSectionCount = sectionCounts.IndexedSectionCount || 0;
    const statusCount = (statusId: number) => aiReferences.filter(reference => reference.AIProcessingStatusId === statusId).length;
    const runItems = latestRun?.DocumentLinkProcessingRunItem || [];
    const aiQueuedCount = latestRun
      ? runItems.filter(item => item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.AIQueued).length
      : statusCount(AIProcessingReferenceStatusAllItems.New) + statusCount(AIProcessingReferenceStatusAllItems.Reprocess);
    const queuedCount = latestRun
      ? runItems.filter(item => item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.Pending
        || item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.AIQueued).length
      : aiQueuedCount;
    const processingCount = latestRun
      ? runItems.filter(item => item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.AIProcessing).length
      : statusCount(AIProcessingReferenceStatusAllItems.Processing);
    const successfulAiReferenceCount = statusCount(AIProcessingReferenceStatusAllItems.Success);
    const failedCount = latestRun ? latestRun.FailedCount || 0 : statusCount(AIProcessingReferenceStatusAllItems.Failed);
    const holdCount = latestRun
      ? runItems.filter(item => item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.OnHold).length
      : statusCount(AIProcessingReferenceStatusAllItems.Hold);
    const indexPercent = vectorEligibleSectionCount ? Math.round((indexedSectionCount / vectorEligibleSectionCount) * 100) : 0;
    const totalLessonCount = latestRun?.TotalLessonCount || 0;
    const evaluatedLessonCount = latestRun?.EvaluatedLessonCount || 0;
    const pendingEvaluationCount = Math.max(0, totalLessonCount - evaluatedLessonCount);
    const terminalRunItemStatusIds = [
      DocumentLinkProcessingRunItemStatusAllItems.DirectLinked,
      DocumentLinkProcessingRunItemStatusAllItems.AILinked,
      DocumentLinkProcessingRunItemStatusAllItems.SkippedActive,
      DocumentLinkProcessingRunItemStatusAllItems.SkippedDisabled,
      DocumentLinkProcessingRunItemStatusAllItems.NoMatch,
      DocumentLinkProcessingRunItemStatusAllItems.Failed,
      DocumentLinkProcessingRunItemStatusAllItems.OnHold
    ];
    const runIsComplete = !!latestRun && [
      DocumentLinkProcessingRunStatusAllItems.Complete,
      DocumentLinkProcessingRunStatusAllItems.CompleteWithErrors,
      DocumentLinkProcessingRunStatusAllItems.Failed,
      DocumentLinkProcessingRunStatusAllItems.Cancelled
    ].includes(latestRun.DocumentLinkProcessingRunStatusId || 0);
    const completedLessonCount = Math.min(totalLessonCount, runIsComplete
      ? totalLessonCount
      : runItems.filter(item => terminalRunItemStatusIds.includes(item.DocumentLinkProcessingRunItemStatusId || 0)).length);
    const overallProcessingPercent = totalLessonCount
      ? Math.min(100, Math.round((completedLessonCount / totalLessonCount) * 100))
      : 0;
    const estimate = this.calculateProcessingEstimate(
      latestRun?.StartedDateTime,
      completedLessonCount,
      totalLessonCount,
      runIsComplete
    );
    const initialEvaluationRunning = !!latestRun && pendingEvaluationCount > 0
      && (latestRun.DocumentLinkProcessingRunStatusId === DocumentLinkProcessingRunStatusAllItems.Queued
        || latestRun.DocumentLinkProcessingRunStatusId === DocumentLinkProcessingRunStatusAllItems.Processing);
    const stages = this.buildStages(!!publication?.CurrentDocumentVersionId, sectionCount, vectorEligibleSectionCount,
      indexedSectionCount, nonVectorSectionCount,
      directLinkCount, aiLinkCount, successfulAiReferenceCount, aiQueuedCount, processingCount, failedCount, initialEvaluationRunning);
    const lastUpdatedDateTime = latestRun?.LastUpdatedDateTime || aiReferences
      .map(reference => reference.ModifiedDateTime)
      .filter((value): value is string => !!value)
      .sort()
      .pop() || publication?.ModifiedDateTime;

    return {
      DocumentItemId: documentItemIdOrDefault(documentItem.DocumentItemId),
      DocumentItemName: documentItem.DocumentItemName,
      PublicationName: publication?.PublicationName,
      PublicationCode: publication?.PublicationCode,
      PublicationType: publication?.DocumentPublicationType?.DocumentPublicationType || publication?.DocumentPublicationTypeIdDisplay,
      VersionLabel: publication?.CurrentDocumentVersion?.VersionLabel,
      SourcePublishedDate: publication?.CurrentDocumentVersion?.SourcePublishedDate,
      LastUpdatedDateTime: lastUpdatedDateTime,
      SectionCount: sectionCount,
      VectorEligibleSectionCount: vectorEligibleSectionCount,
      NonVectorSectionCount: nonVectorSectionCount,
      IndexedSectionCount: indexedSectionCount,
      IndexPercent: indexPercent,
      LinkedCourseCount: courses.length,
      LinkRecordCount: links.length,
      DirectLinkCount: directLinkCount,
      AiLinkCount: aiLinkCount,
      TotalLessonCount: totalLessonCount,
      EvaluatedLessonCount: evaluatedLessonCount,
      PendingEvaluationCount: pendingEvaluationCount,
      CompletedLessonCount: completedLessonCount,
      OverallProcessingPercent: overallProcessingPercent,
      EstimatedCompletedDateTime: estimate.EstimatedCompletedDateTime,
      EstimatedTimeRemaining: estimate.EstimatedTimeRemaining,
      QueuedCount: queuedCount,
      ProcessingCount: processingCount,
      FailedCount: failedCount,
      HoldCount: holdCount,
      Stages: stages,
      Courses: courses,
      Issues: this.buildIssues(publication, vectorEligibleSectionCount, indexedSectionCount, links, aiReferences),
      IsAuthoritative: !!latestRun,
      ProcessingRunId: latestRun?.DocumentLinkProcessingRunId,
      ProcessingRunStatus: latestRun?.DocumentLinkProcessingRunStatus?.DocumentLinkProcessingRunStatus
        || latestRun?.DocumentLinkProcessingRunStatusIdDisplay,
      ProcessingTrigger: latestRun?.DocumentLinkProcessingTriggerType?.DocumentLinkProcessingTriggerType
        || latestRun?.DocumentLinkProcessingTriggerTypeIdDisplay,
      ProcessingProgressText: latestRun?.ProgressText,
      ProcessingStartedDateTime: latestRun?.StartedDateTime,
      ProcessingCompletedDateTime: latestRun?.CompletedDateTime
    };
  }

  private applyAuthoritativeRunItems(
    coursesById: Map<number, FarAimProcessingCourse>,
    modulesById: Map<number, CourseModuleEntity>,
    lessonsById: Map<number, CourseLessonEntity>,
    runItems: DocumentLinkProcessingRunItemEntity[]
  ): void {
    runItems.forEach(item => {
      if (!item.CourseId || !item.CourseModuleId) { return; }
      const course = coursesById.get(item.CourseId);
      if (!course) { return; }
      const moduleEntity = modulesById.get(item.CourseModuleId);
      const lessonEntity = item.CourseLessonId ? lessonsById.get(item.CourseLessonId) : undefined;
      let module = course.Modules.find(value => value.CourseModuleId === item.CourseModuleId);
      if (!module) {
        module = this.createModule(item.CourseModuleId, moduleEntity?.ModuleName || item.CourseModuleIdDisplay,
          moduleEntity?.ModuleOrder);
        course.Modules.push(module);
      }
      const existingIndex = module.Lessons.findIndex(lesson =>
        (!!item.CourseLessonDocumentLinkId && lesson.CourseLessonDocumentLinkId === item.CourseLessonDocumentLinkId)
        || lesson.CourseLessonId === item.CourseLessonId);
      const status = this.mapRunItemStatus(item.DocumentLinkProcessingRunItemStatusId,
        item.DocumentLinkProcessingRunItemStatus?.DocumentLinkProcessingRunItemStatus || item.DocumentLinkProcessingRunItemStatusIdDisplay);
      const lesson: FarAimProcessingLesson = {
        CourseLessonDocumentLinkId: item.CourseLessonDocumentLinkId,
        CourseLessonId: item.CourseLessonId,
        LessonName: lessonEntity?.LessonName || item.CourseLessonIdDisplay,
        LessonOrder: lessonEntity?.LessonOrder,
        LinkStatus: status,
        LinkStatusTone: item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.Failed ? 'warning'
          : item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.AIProcessing ? 'active'
          : item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.Pending
            || item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.AIQueued ? 'pending' : 'complete',
        LinkSource: item.DocumentLinkSourceTypeId === 2 ? 'AI' : item.DocumentLinkSourceTypeId ? 'Direct' : 'Pending',
        AIProcessingReferenceId: item.AIProcessingReferenceId,
        AIProcessingStatusId: item.AIProcessingReference?.AIProcessingStatusId,
        AIProcessingStatus: item.AIProcessingReference?.AIProcessingStatusIdDisplay,
        AIConfidence: item.CourseLessonDocumentLink?.AIConfidence,
        AIMatchReason: item.CourseLessonDocumentLink?.AIMatchReason,
        ReferenceSummaryText: item.ProgressText || item.ErrorText,
        ReferenceCount: (item.CourseLessonDocumentLink?.CourseLessonDocumentLinkReference || []).length,
        IsProblem: item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.Failed
          || item.DocumentLinkProcessingRunItemStatusId === DocumentLinkProcessingRunItemStatusAllItems.NoMatch,
        SearchText: `${lessonEntity?.LessonName || ''} ${item.CourseLessonId || ''} ${status}`.toLowerCase()
      };
      if (existingIndex >= 0) { module.Lessons[existingIndex] = lesson; } else { module.Lessons.push(lesson); }
    });
  }

  private mapRunItemStatus(statusId?: number, fallback?: string): string {
    if (statusId === DocumentLinkProcessingRunItemStatusAllItems.Pending) { return 'Queued'; }
    if (statusId === DocumentLinkProcessingRunItemStatusAllItems.DirectLinked) { return 'Linked'; }
    if (statusId === DocumentLinkProcessingRunItemStatusAllItems.AILinked) { return 'AI linked'; }
    if (statusId === DocumentLinkProcessingRunItemStatusAllItems.AIQueued) { return 'Queued'; }
    if (statusId === DocumentLinkProcessingRunItemStatusAllItems.AIProcessing) { return 'Processing'; }
    if (statusId === DocumentLinkProcessingRunItemStatusAllItems.Failed) { return 'Failed'; }
    if (statusId === DocumentLinkProcessingRunItemStatusAllItems.OnHold) { return 'On hold'; }
    if (statusId === DocumentLinkProcessingRunItemStatusAllItems.NoMatch) { return 'Not linked'; }
    return fallback || 'Pending';
  }

  private mapLesson(
    link: CourseLessonDocumentLinkEntity,
    lessonEntity?: CourseLessonEntity,
    aiReference?: AIProcessingReferenceEntity
  ): FarAimProcessingLesson {
    const source = link.AIConfidence ? 'AI' : 'Direct';
    const isInactive = link.CourseLessonDocumentLinkStatusId !== ReferenceStatusAllItems.Active;
    const aiStatusId = aiReference?.AIProcessingStatusId;
    let status = isInactive ? 'Not linked' : source === 'Direct' ? 'Linked' : 'AI linked';
    let tone: FarAimProcessingLesson['LinkStatusTone'] = isInactive ? 'pending' : 'complete';

    if (aiStatusId === AIProcessingReferenceStatusAllItems.New || aiStatusId === AIProcessingReferenceStatusAllItems.Reprocess) {
      status = 'Queued';
      tone = 'pending';
    } else if (aiStatusId === AIProcessingReferenceStatusAllItems.Processing) {
      status = 'Processing';
      tone = 'active';
    } else if (aiStatusId === AIProcessingReferenceStatusAllItems.Failed) {
      status = 'Failed';
      tone = 'warning';
    } else if (aiStatusId === AIProcessingReferenceStatusAllItems.Hold) {
      status = 'On hold';
      tone = 'warning';
    }

    return {
      CourseLessonDocumentLinkId: link.CourseLessonDocumentLinkId,
      CourseLessonId: link.CourseLessonId,
      LessonName: lessonEntity?.LessonName || link.CourseLessonIdDisplay,
      LessonOrder: lessonEntity?.LessonOrder,
      LinkStatus: status,
      LinkStatusTone: tone,
      LinkSource: source,
      AIProcessingReferenceId: aiReference?.AIProcessingReferenceId,
      AIProcessingStatusId: aiStatusId,
      AIProcessingStatus: aiReference?.AIProcessingStatusIdDisplay,
      AIConfidence: link.AIConfidence,
      AIMatchReason: link.AIMatchReason,
      ReferenceSummaryText: link.ReferenceSummaryText,
      ReferenceCount: (link.CourseLessonDocumentLinkReference || []).length,
      IsProblem: tone === 'warning' || status === 'Not linked',
      SearchText: `${lessonEntity?.LessonName || ''} ${link.CourseLessonId || ''} ${status} ${source}`.toLowerCase()
    };
  }

  private buildStages(
    hasVersion: boolean,
    sectionCount: number,
    vectorEligibleSectionCount: number,
    indexedSectionCount: number,
    nonVectorSectionCount: number,
    directLinkCount: number,
    aiLinkCount: number,
    successfulAiReferenceCount: number,
    queuedCount: number,
    processingCount: number,
    failedCount: number,
    initialEvaluationRunning: boolean
  ): FarAimProcessingStage[] {
    const indexPercent = vectorEligibleSectionCount ? Math.round((indexedSectionCount / vectorEligibleSectionCount) * 100) : 0;
    const completedAiCount = Math.max(aiLinkCount, successfulAiReferenceCount);
    const aiTotal = completedAiCount + queuedCount + processingCount + failedCount;
    const aiCompletePercent = aiTotal ? Math.round((completedAiCount / aiTotal) * 100) : 0;
    return [
      {
        Key: 'structured-import', Label: 'Structured Import', Status: hasVersion ? 'Complete' : 'Not started',
        Detail: hasVersion ? `${sectionCount} active sections` : 'No current structured version', Percent: hasVersion ? 100 : 0,
        Tone: hasVersion ? 'complete' : 'pending'
      },
      {
        Key: 'vector-indexing', Label: 'Vector Indexing',
        Status: indexPercent === 100 && vectorEligibleSectionCount ? 'Complete' : indexedSectionCount ? 'Partial' : 'Not started',
        Detail: `${indexedSectionCount} of ${vectorEligibleSectionCount} eligible sections indexed`
          + (nonVectorSectionCount ? ` / ${nonVectorSectionCount} structural-only` : ''),
        Percent: indexPercent,
        Tone: indexPercent === 100 && vectorEligibleSectionCount ? 'complete' : 'pending'
      },
      {
        Key: 'direct-linking', Label: 'Direct Links',
        Status: initialEvaluationRunning ? 'Evaluating' : directLinkCount ? 'Available' : 'No matches',
        Detail: initialEvaluationRunning ? `${directLinkCount} deterministic links found so far` : `${directLinkCount} active deterministic links`,
        Percent: initialEvaluationRunning ? 0 : directLinkCount ? 100 : 0,
        Tone: initialEvaluationRunning ? 'active' : directLinkCount ? 'complete' : 'pending'
      },
      {
        Key: 'ai-linking', Label: 'AI Lesson Links',
        Status: failedCount ? 'Needs attention' : processingCount || queuedCount ? 'In progress'
          : initialEvaluationRunning ? 'Waiting for evaluation' : aiLinkCount ? 'Complete' : 'Not started',
        Detail: `${aiLinkCount} linked / ${queuedCount} queued / ${processingCount} processing / ${failedCount} failed`,
        Percent: aiCompletePercent,
        Tone: failedCount ? 'warning' : processingCount || queuedCount || initialEvaluationRunning ? 'active'
          : aiLinkCount ? 'complete' : 'pending'
      }
    ];
  }

  private createCourse(courseId: number, courseName?: string): FarAimProcessingCourse {
    return { CourseId: courseId, CourseName: courseName, DirectLinkCount: 0, AiLinkCount: 0, QueuedCount: 0,
      ProcessingCount: 0, FailedCount: 0, Modules: [], SearchText: `${courseName || ''} ${courseId}`.toLowerCase() };
  }

  private createModule(courseModuleId: number, moduleName?: string, moduleOrder?: number): FarAimProcessingModule {
    return { CourseModuleId: courseModuleId, ModuleName: moduleName, ModuleOrder: moduleOrder, DirectLinkCount: 0,
      AiLinkCount: 0, QueuedCount: 0, ProcessingCount: 0, FailedCount: 0, Lessons: [],
      SearchText: `${moduleName || ''} ${courseModuleId}`.toLowerCase() };
  }

  private incrementCounts(target: FarAimProcessingCourse | FarAimProcessingModule, lesson: FarAimProcessingLesson): void {
    if (lesson.LinkSource === 'Direct' && lesson.LinkStatus === 'Linked') {
      target.DirectLinkCount++;
    } else if (lesson.LinkSource === 'AI' && lesson.LinkStatus === 'AI linked') {
      target.AiLinkCount++;
    }
    this.incrementWorkflowCounts(target, lesson);
  }

  private incrementWorkflowCounts(target: FarAimProcessingCourse | FarAimProcessingModule, lesson: FarAimProcessingLesson): void {
    if (lesson.LinkStatus === 'Queued') { target.QueuedCount++; }
    if (lesson.LinkStatus === 'Processing') { target.ProcessingCount++; }
    if (lesson.LinkStatus === 'Failed') { target.FailedCount++; }
  }

  private buildIssues(
    publication: DocumentPublicationEntity | undefined,
    sectionCount: number,
    indexedSectionCount: number,
    links: CourseLessonDocumentLinkEntity[],
    aiReferences: AIProcessingReferenceEntity[]
  ): FarAimProcessingIssue[] {
    const issues: FarAimProcessingIssue[] = [];
    if (!publication?.CurrentDocumentVersionId) {
      issues.push({ Severity: 'error', Title: 'No current structured version',
        Detail: 'Lesson linking cannot be evaluated reliably until the publication has a current version.' });
    }
    if (sectionCount && indexedSectionCount < sectionCount) {
      issues.push({ Severity: 'warning', Title: 'Vector indexing is incomplete',
        Detail: `${sectionCount - indexedSectionCount} active sections do not have a vector reference.` });
    }
    const inactiveLinkCount = links.filter(link => link.CourseLessonDocumentLinkStatusId === ReferenceStatusAllItems.Inactive).length;
    if (inactiveLinkCount) {
      issues.push({ Severity: 'warning', Title: 'Lessons without active references',
        Detail: `${inactiveLinkCount} link records are inactive or have not produced a successful match.` });
    }
    const failedCount = aiReferences.filter(reference => reference.AIProcessingStatusId === AIProcessingReferenceStatusAllItems.Failed).length;
    if (failedCount) {
      issues.push({ Severity: 'error', Title: 'AI processing failures',
        Detail: `${failedCount} AI processing references need attention.` });
    }
    return issues;
  }

  private calculateProcessingEstimate(
    startedDateTime: string | undefined,
    completedCount: number,
    totalCount: number,
    isComplete: boolean
  ): { EstimatedCompletedDateTime?: string; EstimatedTimeRemaining?: string } {
    if (!startedDateTime || isComplete || completedCount <= 0 || completedCount >= totalCount) {
      return {};
    }

    const startedMilliseconds = new Date(startedDateTime).getTime();
    const nowMilliseconds = Date.now();
    if (!isFinite(startedMilliseconds) || startedMilliseconds >= nowMilliseconds) {
      return {};
    }

    const remainingMilliseconds = Math.round(
      ((nowMilliseconds - startedMilliseconds) / completedCount) * (totalCount - completedCount)
    );
    const estimatedCompletedDateTime = new Date(nowMilliseconds + remainingMilliseconds).toISOString();
    const remainingMinutes = Math.max(1, Math.round(remainingMilliseconds / 60000));
    const estimatedTimeRemaining = remainingMinutes < 60
      ? `about ${remainingMinutes} min remaining`
      : remainingMinutes < 1440
        ? `about ${Math.round(remainingMinutes / 60)} hr remaining`
        : `about ${Math.round(remainingMinutes / 1440)} days remaining`;

    return {
      EstimatedCompletedDateTime: estimatedCompletedDateTime,
      EstimatedTimeRemaining: estimatedTimeRemaining
    };
  }
}

function documentItemIdOrDefault(documentItemId?: number): number {
  return documentItemId || 0;
}
